/**
 * 辞书分类管理 API
 *
 * 提供辞书分类和子分类的管理操作
 */

import httpRequest from '@/utils/api'
import type {
  CategoryWithSubCategories,
  SubCategory,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CreateSubCategoryRequest,
  UpdateSubCategoryRequest,
  CategoryReorderRequest,
  SubCategoryReorderRequest,
  CategoryListResponseData,
} from '@/types/dictation'

/**
 * API 路径枚举
 */
enum API {
  /** 主分类列表 */
  categories = '/dictation-api/admin/dictation/categories',
  /** 单个主分类 */
  category = '/dictation-api/admin/dictation/categories/:categoryId',
  /** 主分类排序 */
  categoryReorder = '/dictation-api/admin/dictation/categories/reorder',
  /** 子分类列表 */
  subcategories = '/dictation-api/admin/dictation/categories/:categoryId/subcategories',
  /** 单个子分类 */
  subcategory = '/dictation-api/admin/dictation/subcategories/:subcategoryId',
  /** 子分类排序 */
  subcategoryReorder = '/dictation-api/admin/dictation/categories/:categoryId/subcategories/reorder',
}

/**
 * 获取主分类列表
 *
 * @param includeSubcategories 是否包含子分类
 * @param includeStats 是否包含统计信息
 * @returns 主分类列表响应
 */
export const getCategoryListAPI = (
  includeSubcategories = true,
  includeStats = true
) => {
  return httpRequest.get<CategoryListResponseData>({
    url: API.categories,
    params: {
      include_subcategories: includeSubcategories,
      include_stats: includeStats,
    },
  })
}

/**
 * 获取单个主分类详情
 *
 * @param categoryId 主分类 ID
 * @returns 主分类详情响应
 */
export const getCategoryAPI = (categoryId: number) => {
  return httpRequest.get<CategoryWithSubCategories>({
    url: API.category.replace(':categoryId', categoryId.toString()),
  })
}

/**
 * 创建主分类
 *
 * @param data 创建请求数据
 * @returns 主分类详情响应
 */
export const createCategoryAPI = (data: CreateCategoryRequest) => {
  return httpRequest.post<CategoryWithSubCategories>({
    url: API.categories,
    data,
  })
}

/**
 * 更新主分类
 *
 * @param categoryId 主分类 ID
 * @param data 更新请求数据
 * @returns 主分类详情响应
 */
export const updateCategoryAPI = (
  categoryId: number,
  data: UpdateCategoryRequest
) => {
  return httpRequest.put<CategoryWithSubCategories>({
    url: API.category.replace(':categoryId', categoryId.toString()),
    data,
  })
}

/**
 * 删除主分类
 *
 * @param categoryId 主分类 ID
 * @returns 删除响应
 */
export const deleteCategoryAPI = (categoryId: number) => {
  return httpRequest.delete<void>({
    url: API.category.replace(':categoryId', categoryId.toString()),
  })
}

/**
 * 批量更新主分类排序
 *
 * @param data 排序请求数据
 * @returns 更新响应
 */
export const reorderCategoriesAPI = (data: CategoryReorderRequest) => {
  return httpRequest.patch<void>({
    url: API.categoryReorder,
    data,
  })
}

/**
 * 获取子分类列表
 *
 * @param categoryId 主分类 ID
 * @returns 子分类列表响应
 */
export const getSubCategoryListAPI = (categoryId: number) => {
  return httpRequest.get<{ subcategories: SubCategory[]; total: number }>({
    url: API.subcategories.replace(':categoryId', categoryId.toString()),
  })
}

/**
 * 获取单个子分类详情
 *
 * @param subcategoryId 子分类 ID
 * @returns 子分类详情响应
 */
export const getSubCategoryAPI = (subcategoryId: number) => {
  return httpRequest.get<SubCategory>({
    url: API.subcategory.replace(':subcategoryId', subcategoryId.toString()),
  })
}




/**
 * 创建子分类
 *
 * @param categoryId 主分类 ID
 * @param data 创建请求数据
 * @returns 子分类详情响应
 */
export const createSubCategoryAPI = (
  categoryId: number,
  data: CreateSubCategoryRequest
) => {
  return httpRequest.post<SubCategory>({
    url: API.subcategories.replace(':categoryId', categoryId.toString()),
    data,
  })
}

/**
 * 更新子分类
 *
 * @param subcategoryId 子分类 ID
 * @param data 更新请求数据
 * @returns 子分类详情响应
 */
export const updateSubCategoryAPI = (
  subcategoryId: number,
  data: UpdateSubCategoryRequest
) => {
  return httpRequest.put<SubCategory>({
    url: API.subcategory.replace(':subcategoryId', subcategoryId.toString()),
    data,
  })
}

/**
 * 删除子分类
 *
 * @param subcategoryId 子分类 ID
 * @returns 删除响应
 */
export const deleteSubCategoryAPI = (subcategoryId: number) => {
  return httpRequest.delete<void>({
    url: API.subcategory.replace(':subcategoryId', subcategoryId.toString()),
  })
}

/**
 * 批量更新子分类排序
 *
 * @param categoryId 主分类 ID
 * @param data 排序请求数据
 * @returns 更新响应
 */
export const reorderSubCategoriesAPI = (
  categoryId: number,
  data: SubCategoryReorderRequest
) => {
  return httpRequest.patch<void>({
    url: API.subcategoryReorder.replace(':categoryId', categoryId.toString()),
    data,
  })
}
